import DashboardHeader from "@/components/DashboardHeader";
import SearchSelect from "@/components/StyledSelect/SearchSelect";
import Alert from "@/components/alert/Alert";
import { useAppSelector } from "@/hooks/hook";
import { GET } from "@/services/api";
import { API_ROUTES } from "@/services/environment";
import { Button, Loader } from "@thdinc/hpf-shared";
import React, { useEffect, useRef, useState } from "react";
import PaginationRow from "@/components/paginationRow/PaginationRow";
import Tabs from "@/components/tabs/Tabs";
import AddPointsModal from "./components/AddPointsModal";
import CampaignsModal from "./components/CampaignsModal";
import CashflowModal from "./components/CashflowModal";
import CashflowHistoryTable from "./components/CashflowHistoryTable";
import CashflowSourceFilterSelect from "./components/CashflowSourceFilterSelect";
import CurrentBalanceCard from "./components/CurrentBalanceCard";
import WaivedProgressCard from "./components/WaivedProgressCard";
import TotalPointsEarnedCard from "./components/TotalPointsEarnedCard";
import SourceFilterSelect from "./components/SourceFilterSelect";
import PointsHistoryTable from "./components/PointsHistoryTable";
import { BusinessOption, CashflowLog, CashflowSource, RewardPointsLog, RewardPointsSource } from "./interface";

const LIMIT = 10;

const TABS = ["Point History", "Cash History"] as const;
type RewardPointsTab = (typeof TABS)[number];

const RewardPointsPage = () => {
    const { refreshToken, business_id, roles } = useAppSelector((store) => store.user);
    const {
        getActiveCompanyApi,
        getRewardPointsGlobalApi,
        getRewardPointsBalanceApi,
        getRewardPointsLogsApi,
        getCashflowLogsApi,
        billingDetailsApi,
    } = API_ROUTES;
    const isSuperAdmin = roles === "admin";

    const [businesses, setBusinesses] = useState<BusinessOption[]>([]);
    const [currBusiness, setCurrBusiness] = useState<BusinessOption | null>(null);
    const [isBusinessLoading, setIsBusinessLoading] = useState<boolean>(false);

    const [balance, setBalance] = useState<number | null>(null);
    const [totalEarnedPoints, setTotalEarnedPoints] = useState<number>(0);
    const [monthlyWaiverThreshold, setMonthlyWaiverThreshold] = useState<number>(0);
    const [nextBillingDate, setNextBillingDate] = useState<string | null>(null);
    const [logs, setLogs] = useState<RewardPointsLog[]>([]);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [hasNext, setHasNext] = useState<boolean>(false);
    const [totalResults, setTotalResults] = useState<number>(0);
    const [sourceFilter, setSourceFilter] = useState<RewardPointsSource | "">("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [refreshKey, setRefreshKey] = useState<number>(0);

    const [activeTab, setActiveTab] = useState<RewardPointsTab>("Point History");
    const [cashflowLogs, setCashflowLogs] = useState<CashflowLog[]>([]);
    const [cashflowPageNumber, setCashflowPageNumber] = useState<number>(1);
    const [cashflowHasNext, setCashflowHasNext] = useState<boolean>(false);
    const [cashflowTotalResults, setCashflowTotalResults] = useState<number>(0);
    const [cashflowSourceFilter, setCashflowSourceFilter] = useState<CashflowSource | "">("");
    const [isCashflowLoading, setIsCashflowLoading] = useState<boolean>(false);
    const [cashflowRefreshKey, setCashflowRefreshKey] = useState<number>(0);

    const [isAddPointsModalOpen, setIsAddPointsModalOpen] = useState<boolean>(false);
    const [isCampaignsModalOpen, setIsCampaignsModalOpen] = useState<boolean>(false);
    const [isCashflowModalOpen, setIsCashflowModalOpen] = useState<boolean>(false);
    const [isManageMenuOpen, setIsManageMenuOpen] = useState<boolean>(false);
    const manageMenuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (manageMenuRef.current && !manageMenuRef.current.contains(event.target as Node)) {
                setIsManageMenuOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (isSuperAdmin) {
            (async () => {
                try {
                    setIsBusinessLoading(true);
                    const result = await GET(getActiveCompanyApi, refreshToken);
                    if (result?.data) {
                        setBusinesses(result.data);
                        const ownBusiness = result.data.find((b: BusinessOption) => b.id === business_id);
                        setCurrBusiness((prev) => prev ?? ownBusiness ?? result.data[0] ?? null);
                    }
                } catch (error) {
                    console.log("get active business list error:", error);
                } finally {
                    setIsBusinessLoading(false);
                }
            })();
        } else if (business_id) {
            setCurrBusiness({ id: business_id, name: "My Business" });
        }
    }, [isSuperAdmin, business_id]);

    // Reset to page 1 whenever the selected business or source filter changes.
    useEffect(() => {
        setPageNumber(1);
    }, [currBusiness, sourceFilter]);

    useEffect(() => {
        setCashflowPageNumber(1);
    }, [currBusiness, cashflowSourceFilter]);

    useEffect(() => {
        if (!currBusiness?.id) return;

        (async () => {
            try {
                setIsLoading(true);
                const balanceResult = await GET(`${getRewardPointsBalanceApi}/${currBusiness.id}`, refreshToken);
                setBalance(balanceResult?.data?.record?.total_points ?? 0);
                setTotalEarnedPoints(balanceResult?.data?.record?.total_earned_points ?? 0);

                const query = new URLSearchParams({
                    limit: String(LIMIT),
                    pageNumber: String(pageNumber),
                });
                if (sourceFilter) query.set("source", sourceFilter);

                const logsResult = await GET(
                    `${getRewardPointsLogsApi}/${currBusiness.id}?${query.toString()}`,
                    refreshToken
                );
                setLogs(logsResult?.data?.result ?? []);
                setHasNext(Boolean(logsResult?.data?.hasNext));
                setTotalResults(logsResult?.data?.totalResults ?? 0);
            } catch (error) {
                console.log("get reward points error:", error);
                Alert({ type: "error", message: "Unable to fetch reward points" });
            } finally {
                setIsLoading(false);
            }
        })();
    }, [currBusiness, pageNumber, sourceFilter, refreshKey]);

    useEffect(() => {
        if (!currBusiness?.id || activeTab !== "Cash History") return;

        (async () => {
            try {
                setIsCashflowLoading(true);

                const query = new URLSearchParams({
                    limit: String(LIMIT),
                    pageNumber: String(cashflowPageNumber),
                });
                if (cashflowSourceFilter) query.set("source", cashflowSourceFilter);

                const cashflowResult = await GET(
                    `${getCashflowLogsApi}/${currBusiness.id}?${query.toString()}`,
                    refreshToken
                );
                setCashflowLogs(cashflowResult?.data?.result ?? []);
                setCashflowHasNext(Boolean(cashflowResult?.data?.hasNext));
                setCashflowTotalResults(cashflowResult?.data?.totalResults ?? 0);
            } catch (error) {
                console.log("get cashflow logs error:", error);
                Alert({ type: "error", message: "Unable to fetch cash flow details" });
            } finally {
                setIsCashflowLoading(false);
            }
        })();
    }, [currBusiness, cashflowPageNumber, cashflowSourceFilter, cashflowRefreshKey, activeTab]);

    useEffect(() => {
        if (!currBusiness?.id) return;

        (async () => {
            try {
                const billingResult = await GET(`${billingDetailsApi}/${currBusiness.id}`, refreshToken);
                setNextBillingDate(billingResult?.data?.[0]?.next_billing_date ?? null);
            } catch (error) {
                console.log("get billing info error:", error);
                setNextBillingDate(null);
            }
        })();
    }, [currBusiness]);

    useEffect(() => {
        if (!currBusiness?.id) return;

        (async () => {
            try {
                const globalResult = await GET(`${getRewardPointsGlobalApi}/${currBusiness.id}`, refreshToken);
                setMonthlyWaiverThreshold(globalResult?.data?.record?.waived_points ?? 0);
            } catch (error) {
                console.log("get reward points global settings error:", error);
                setMonthlyWaiverThreshold(0);
            }
        })();
    }, [currBusiness]);

    const currentStart = totalResults === 0 ? 0 : (pageNumber - 1) * LIMIT + 1;
    const currentEnd = Math.min(pageNumber * LIMIT, totalResults);

    const cashflowCurrentStart = cashflowTotalResults === 0 ? 0 : (cashflowPageNumber - 1) * LIMIT + 1;
    const cashflowCurrentEnd = Math.min(cashflowPageNumber * LIMIT, cashflowTotalResults);

    return (
        <div>
            <div className="flex items-start md:items-center gap-1 flex-wrap justify-between mt-2 sticky top-[64px] bg-p-gray z-30 pb-1">
                <DashboardHeader width="w-full" title="Reward Points">
                    {isSuperAdmin && (
                        <div className="w-full md:w-auto mt-4 md:mt-0 flex flex-col sm:flex-row gap-3">
                            <SearchSelect
                                height={52}
                                width={220}
                                options={businesses}
                                value={currBusiness ?? undefined}
                                onChange={(selected) => setCurrBusiness(selected)}
                                placeholder="Select Business"
                                backgroundColor="white"
                                isLoading={isBusinessLoading}
                            />
                            <Button
                                type="button"
                                disabled={!currBusiness}
                                onClick={() => setIsCampaignsModalOpen(true)}
                                className="bg-white text-black border border-gray-300 rounded-full py-3 px-6 text-sm font-medium whitespace-nowrap"
                            >
                                Campaigns
                            </Button>
                            <div className="relative w-full sm:w-auto flex" ref={manageMenuRef}>
                                <Button
                                    type="button"
                                    disabled={!currBusiness}
                                    onClick={() => setIsManageMenuOpen((prev) => !prev)}
                                    className="w-full sm:w-auto bg-black text-white rounded-full py-3 px-6 text-sm font-medium whitespace-nowrap"
                                >
                                    Manage
                                </Button>
                                {isManageMenuOpen && (
                                    <div className="absolute right-0 mt-2 w-40 rounded-lg bg-white shadow-lg border z-50">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setIsAddPointsModalOpen(true);
                                                setIsManageMenuOpen(false);
                                            }}
                                            className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                                        >
                                            Points
                                        </button>
                                        <div className="border-t border-gray-200" />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setIsCashflowModalOpen(true);
                                                setIsManageMenuOpen(false);
                                            }}
                                            className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                                        >
                                            Cashflow
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </DashboardHeader>
                <p className="text-sm text-gray-500 max-w-xl -mt-3">
                    Companies earn points from platform fees and preferred-brand purchases.
                </p>
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <CurrentBalanceCard balance={balance ?? 0} />
                <WaivedProgressCard
                    label="Next Month Waived"
                    currentPoints={balance ?? 0}
                    threshold={monthlyWaiverThreshold}
                    nextBillingDate={nextBillingDate}
                />
                <TotalPointsEarnedCard
                    totalPoints={totalEarnedPoints}
                />
            </div>

            <div className="mt-6">
                <Tabs tabs={[...TABS]} activeTab={activeTab} onTabChange={(tab) => setActiveTab(tab as RewardPointsTab)} />
            </div>

            {activeTab === "Point History" ? (
                <>
                    <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
                        <h2 className="text-lg font-semibold text-gray-800">Points history</h2>
                        <SourceFilterSelect value={sourceFilter} onChange={setSourceFilter} />
                    </div>

                    {isLoading ? (
                        <Loader color="bg-red-500" />
                    ) : logs.length > 0 ? (
                        <>
                            <PointsHistoryTable logs={logs} />
                            <PaginationRow
                                currentStart={currentStart}
                                currentEnd={currentEnd}
                                total={totalResults}
                                disablePrev={pageNumber <= 1}
                                disableNext={!hasNext}
                                onPrev={() => setPageNumber((p) => Math.max(1, p - 1))}
                                onNext={() => setPageNumber((p) => p + 1)}
                            />
                        </>
                    ) : (
                        <p className="text-center text-gray-500 text-lg mt-4">No reward point activity found</p>
                    )}
                </>
            ) : (
                <>
                    <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
                        <h2 className="text-lg font-semibold text-gray-800">Cash history</h2>
                        <CashflowSourceFilterSelect value={cashflowSourceFilter} onChange={setCashflowSourceFilter} />
                    </div>

                    {isCashflowLoading ? (
                        <Loader color="bg-red-500" />
                    ) : cashflowLogs.length > 0 ? (
                        <>
                            <CashflowHistoryTable logs={cashflowLogs} />
                            <PaginationRow
                                currentStart={cashflowCurrentStart}
                                currentEnd={cashflowCurrentEnd}
                                total={cashflowTotalResults}
                                disablePrev={cashflowPageNumber <= 1}
                                disableNext={!cashflowHasNext}
                                onPrev={() => setCashflowPageNumber((p) => Math.max(1, p - 1))}
                                onNext={() => setCashflowPageNumber((p) => p + 1)}
                            />
                        </>
                    ) : (
                        <p className="text-center text-gray-500 text-lg mt-4">No cash flow activity found</p>
                    )}
                </>
            )}

            {currBusiness && (
                <AddPointsModal
                    isOpen={isAddPointsModalOpen}
                    onClose={() => setIsAddPointsModalOpen(false)}
                    businessId={currBusiness.id}
                    businesses={businesses}
                    onSuccess={() => {
                        setPageNumber(1);
                        setRefreshKey((key) => key + 1);
                    }}
                />
            )}

            <CampaignsModal
                isOpen={isCampaignsModalOpen}
                onClose={() => setIsCampaignsModalOpen(false)}
            />

            {currBusiness && (
                <CashflowModal
                    isOpen={isCashflowModalOpen}
                    onClose={() => setIsCashflowModalOpen(false)}
                    businessId={currBusiness.id}
                    businesses={businesses}
                    onSuccess={() => {
                        setCashflowPageNumber(1);
                        setCashflowRefreshKey((key) => key + 1);
                    }}
                />
            )}
        </div>
    );
};

export default RewardPointsPage;
